






























































































































































































































































































import { PropType } from 'vue';
import FormInputCount from '@/components/molecules/formInputCount/FormInputCount.vue';
import FormInputText from '@/components/atoms/formInputText/FormInputText.vue';
import FormField from '@/components/molecules/formField/FormField.vue';
import FormFileUpload from '@/components/molecules/formFileUpload/FormFileUpload.vue';
import FunctionalityButton, {
    FunctionalityButtonValueType,
} from '@/components/molecules/functionalityButton/FunctionalityButton.vue';
import { getFormInputMixin } from '@/mixins/formInputMixin';
import { InverterModuleFormGroup } from '@/types/forms/formGroups/plantPerformance/inverter/inverterModuleFormGroup';
import type { InverterModuleFormGroupContent } from '@/api/interfaces/content/form/steps/plantPerformance/inverterContent';
import type { ValidationsCommonType } from '@/services/form/validations';
import { number } from '@/validators/number';
import { interpolate } from '@/services/interpolationService';
import {
    formatGermanNumber,
    parseGermanFloat,
} from '@/utilities/germanFormats';
import { ApplicationType } from '@/types/forms/specific/applicationForm';
import { roundToDecimals } from '@/utilities/math';
import FormSelect from '@/components/molecules/formSelect/FormSelect.vue';
import FormTextBlock from '@/components/molecules/formTextBlock/FormTextBlock.vue';
import FormBooleanCheckbox from '@/components/molecules/formBooleanCheckbox/FormBooleanCheckbox.vue';
import { InverterDataContent } from '@/api/interfaces/content/form/serviceDriven/inverterDataContent';
import { contentServices } from '@/services/contentService';
import { InverterDataContentSearchByZerezId } from '@/api/interfaces/content/form/serviceDriven/InverterDataContentSearchByZerezId';
import { isInverterDataContentSearchByManufacturerAndModuleType } from '@/api/interfaces/typeGuards';
import { ConnectionRulesType } from '@/types/forms/shared/connectionRulesType';
import { enumsContent } from '@/api/mock/content/app/enumsContent';
import { camelCase } from 'lodash';
import { EntryListContent } from '@/api/interfaces/content/common/entryListContent';
import { InverterDataContentSearchByManufacturerAndModuleType } from '@/api/interfaces/content/form/serviceDriven/inverterDataContentSearchByManufacturerAndModuleType';
import { appState } from '@/services/form/appState';

export default getFormInputMixin<
    InverterModuleFormGroup,
    InverterModuleFormGroupContent
>().extend({
    name: 'InverterModuleFormGroup',
    components: {
        FormInputCount,
        FormInputText,
        FormField,
        FormFileUpload,
        FunctionalityButton,
        FormSelect,
        FormTextBlock,
        FormBooleanCheckbox,
    },
    props: {
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String as PropType<ApplicationType>,
            required: true,
        },
        showRemoveButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            functionalityButtonValue: 'remove' as FunctionalityButtonValueType,
        };
    },
    computed: {
        inverterManufacturerAndModuleTypeValidations():
            | ValidationsCommonType
            | unknown {
            return this.validations?.inverterManufacturerAndModuleType || {};
        },
        inverterZerezIdAndNormValidations(): ValidationsCommonType | unknown {
            return this.validations?.inverterZerezIdAndNorm || {};
        },
        inverterActivePowerAndApparentPowerValidations():
            | ValidationsCommonType
            | unknown {
            return this.validations?.inverterActivePowerAndApparentPower || {};
        },
        totalActivePowerDisplayValue(): string {
            return interpolate(this.content.inverterTotalActivePower.unit, {
                value: formatGermanNumber(
                    this.model.inverterTotalActivePower.value ?? 0,
                    {
                        decimals: 2,
                        thousandsSeparator: true,
                    },
                ),
            });
        },
        totalApparentPowerDisplayValue(): string {
            return interpolate(this.content.inverterTotalApparentPower.unit, {
                value: formatGermanNumber(
                    this.model.inverterTotalApparentPower.value ?? 0,
                    {
                        decimals: 2,
                        thousandsSeparator: true,
                    },
                ),
            });
        },
        inverterManualInputManufacturerTypeZerezId(): boolean {
            return (
                this.model.inverterManualInputManufacturerTypeZerezId?.value ??
                false
            );
        },
    },
    watch: {
        'model.inverterQuantity.value': {
            handler(newValue: number | null) {
                this.model.inverterTotalActivePower.value = this.getTotalPower(
                    newValue,
                    this.model.inverterActivePowerAndApparentPower.activePower
                        .value,
                );

                this.model.inverterTotalApparentPower.value = this.getTotalPower(
                    newValue,
                    this.model.inverterActivePowerAndApparentPower.apparentPower
                        .value,
                );
            },
            immediate: true,
        },
        'model.inverterActivePowerAndApparentPower.activePower.value': {
            handler(newValue: string | null) {
                this.model.inverterTotalActivePower.value = this.getTotalPower(
                    this.model.inverterQuantity.value,
                    newValue,
                );
            },
            immediate: true,
        },
        'model.inverterActivePowerAndApparentPower.apparentPower.value': {
            handler(newValue: string | null) {
                this.model.inverterTotalApparentPower.value = this.getTotalPower(
                    this.model.inverterQuantity.value,
                    newValue,
                );
            },
            immediate: true,
        },
        'model.inverterManufacturerAndModuleType.manufacturerText.value': {
            handler(newValue: string | null) {
                // On any sort of interaction, sync to backing field
                this.model.inverterManufacturerAndModuleType.manufacturer.value = newValue;
            },
            immediate: false,
        },
        'model.inverterManufacturerAndModuleType.manufacturerSelect.value': {
            handler(newValue: string | null, oldValue: string | null): void {
                // On any sort of interaction, sync to backing field
                this.model.inverterManufacturerAndModuleType.manufacturer.value = newValue;

                // If value didn't change, there is nothing that has to be done
                if (newValue === oldValue) {
                    return;
                }

                // If new value is empty, then this field shouldn't cause changes to ay other fields,
                // except for the aforementioned backing field
                if (!newValue) {
                    return;
                }

                // That's where we end, if the manufacturer name hasn't been set already
                if (
                    !this.model.inverterManufacturerAndModuleType
                        .moduleTypeSelect.value
                ) {
                    return;
                }

                // However, if both are available, try searching for an inverter for auto pre-fill,
                // clearing the other fields, if no match could be found
                const inverter = this.getInverter({
                    manufacturerName: newValue,
                    moduleType: this.model.inverterManufacturerAndModuleType
                        .moduleTypeSelect.value,
                });
                if (inverter === undefined) {
                    this.model.inverterManufacturerAndModuleType.moduleTypeSelect.value = null;
                    this.model.inverterZerezIdAndNorm.zerezId.value = null;
                    this.model.inverterZerezIdAndNorm.norm.value = null;
                    this.model.inverterActivePowerAndApparentPower.activePower.value = null;
                    this.model.inverterActivePowerAndApparentPower.apparentPower.value = null;
                    return;
                }

                this.updateInverterFields(inverter);
            },
            immediate: false,
        },
        'model.inverterManufacturerAndModuleType.moduleTypeText.value': {
            handler(newValue: string | null) {
                // On any sort of interaction, sync to backing field
                this.model.inverterManufacturerAndModuleType.moduleType.value = newValue;
            },
            immediate: false,
        },
        'model.inverterManufacturerAndModuleType.moduleTypeSelect.value': {
            handler(newValue: string | null, oldValue: string | null): void {
                // On any sort of interaction, sync to backing field
                this.model.inverterManufacturerAndModuleType.moduleType.value = newValue;

                // If value didn't change, there is nothing that has to be done
                if (newValue === oldValue) {
                    return;
                }

                // If new value is empty, then this field shouldn't cause changes to ay other fields,
                // except for the aforementioned backing field
                if (!newValue) {
                    return;
                }

                // That's where we end, if the manufacturer name hasn't been set already
                if (
                    !this.model.inverterManufacturerAndModuleType
                        .manufacturerSelect.value
                ) {
                    return;
                }

                // However, if both are available, try searching for an inverter for auto pre-fill,
                // clearing the other fields, if no match could be found
                const inverter = this.getInverter({
                    manufacturerName: this.model
                        .inverterManufacturerAndModuleType.manufacturerSelect
                        .value,
                    moduleType: newValue,
                });
                if (inverter === undefined) {
                    this.model.inverterManufacturerAndModuleType.manufacturerSelect.value = null;
                    this.model.inverterZerezIdAndNorm.zerezId.value = null;
                    this.model.inverterZerezIdAndNorm.norm.value = null;
                    this.model.inverterActivePowerAndApparentPower.activePower.value = null;
                    this.model.inverterActivePowerAndApparentPower.apparentPower.value = null;
                    return;
                }

                this.updateInverterFields(inverter);
            },
            immediate: false,
        },
        'model.inverterZerezIdAndNorm.zerezId.value': {
            handler(newValue: string | null, oldValue: string | null): void {
                // On any sort of interaction, sync to backing field
                this.model.inverterZerezIdAndNorm.zerezId.value = newValue;

                // If value didn't change, there is nothing that has to be done
                if (newValue === oldValue) {
                    return;
                }

                // If new value is empty, then do not cause a watcher loop
                if (!newValue) {
                    return;
                }

                // However, if both are available, try searching for an inverter for auto pre-fill,
                // clearing the other fields, if no match could be found
                const inverter = this.getInverter({ zerezId: newValue });
                if (inverter === undefined) {
                    this.model.inverterManufacturerAndModuleType.manufacturerSelect.value = null;
                    this.model.inverterManufacturerAndModuleType.moduleTypeSelect.value = null;
                    this.model.inverterZerezIdAndNorm.norm.value = null;
                    this.model.inverterActivePowerAndApparentPower.activePower.value = null;
                    this.model.inverterActivePowerAndApparentPower.apparentPower.value = null;
                    return;
                }

                this.updateInverterFields(inverter);
            },
            immediate: false,
        },
        'model.inverterZerezIdAndNorm.norm.value': {
            handler(newValue: ConnectionRulesType[]) {
                if (!newValue) {
                    this.model.inverterZerezIdAndNorm.normText.value = null;
                    return;
                }

                const translated = newValue.map(
                    (value) =>
                        (enumsContent.connectionRulesType as EntryListContent)[
                            camelCase(value)
                        ],
                ) as string[];
                this.model.inverterZerezIdAndNorm.normText.value = translated.join(
                    ', ',
                );
            },
            immediate: true,
        },
        // If the displayed fields are toggled, set the backing fields to the values of the now shown fields and possibly clear irrelevant fields
        inverterManualInputManufacturerTypeZerezId: {
            handler(newValue: boolean) {
                if (appState.debugFillInProgress) {
                    return;
                }

                // Update backing fields and possibly other fields based on which fields are displayed
                if (newValue) {
                    this.model.inverterManufacturerAndModuleType.manufacturer.value = this.model.inverterManufacturerAndModuleType.manufacturerText.value;
                    this.model.inverterManufacturerAndModuleType.moduleType.value = this.model.inverterManufacturerAndModuleType.moduleTypeText.value;
                    this.model.inverterZerezIdAndNorm.zerezId.value = null;
                    this.model.inverterZerezIdAndNorm.norm.value = null;
                    this.model.inverterActivePowerAndApparentPower.activePower.value = null;
                    this.model.inverterActivePowerAndApparentPower.apparentPower.value = null;
                } else {
                    this.model.inverterManufacturerAndModuleType.manufacturer.value = this.model.inverterManufacturerAndModuleType.manufacturerSelect.value;
                    this.model.inverterManufacturerAndModuleType.moduleType.value = this.model.inverterManufacturerAndModuleType.moduleTypeSelect.value;

                    // Reset norm field, if zerez id is not filled
                    if (
                        this.model.inverterZerezIdAndNorm.zerezId.value === null
                    ) {
                        this.model.inverterZerezIdAndNorm.norm.value = null;
                    }

                    // Check currently filled fields, to possibly immediately prefill fields back up again (e.g. someone checked the box, which clears zerez id and norm, and immediately unchecks it again)
                    if (this.model.inverterZerezIdAndNorm.zerezId.value) {
                        const inverter = this.getInverter({
                            zerezId: this.model.inverterZerezIdAndNorm.zerezId
                                .value,
                        });
                        if (inverter) {
                            this.updateInverterFields(inverter);
                        }
                    } else if (
                        this.model.inverterManufacturerAndModuleType
                            .manufacturerSelect.value &&
                        this.model.inverterManufacturerAndModuleType
                            .moduleTypeSelect.value
                    ) {
                        const inverter = this.getInverter({
                            manufacturerName: this.model
                                .inverterManufacturerAndModuleType
                                .manufacturerSelect.value,
                            moduleType: this.model
                                .inverterManufacturerAndModuleType
                                .moduleTypeSelect.value,
                        });
                        if (inverter) {
                            this.updateInverterFields(inverter);
                        }
                    }
                }
            },
            immediate: false,
        },
    },
    created() {
        if (this.inverterManualInputManufacturerTypeZerezId) {
            this.model.inverterManufacturerAndModuleType.manufacturerText.value = this.model.inverterManufacturerAndModuleType.manufacturer.value;
            this.model.inverterManufacturerAndModuleType.moduleTypeText.value = this.model.inverterManufacturerAndModuleType.moduleType.value;
            this.model.inverterZerezIdAndNorm.zerezId.value = null;
        } else {
            this.model.inverterManufacturerAndModuleType.manufacturerSelect.value = this.model.inverterManufacturerAndModuleType.manufacturer.value;
            this.model.inverterManufacturerAndModuleType.moduleTypeSelect.value = this.model.inverterManufacturerAndModuleType.moduleType.value;
        }
    },
    methods: {
        getTotalPower(
            rawQuantity: number | null,
            rawPower: string | null,
        ): number {
            // power as number; default: 0
            let power = 0;
            if (rawPower != null) {
                const parsedPower = parseGermanFloat(rawPower);
                power = number(parsedPower) ? parsedPower : 0;
            }

            // quantity as number; default: 0
            let quantity = 0;
            if (rawQuantity != null) {
                quantity = number(rawQuantity) ? rawQuantity : 0;
            }

            return roundToDecimals(quantity * power);
        },
        getInverter(
            search:
                | InverterDataContentSearchByManufacturerAndModuleType
                | InverterDataContentSearchByZerezId,
        ): InverterDataContent | undefined {
            const content = contentServices.form.content;
            if (!content) {
                return undefined;
            }

            const invertersContent =
                content?.serviceDrivenContent?.invertersContent;
            if (!invertersContent) {
                return undefined;
            }

            if (
                isInverterDataContentSearchByManufacturerAndModuleType(search)
            ) {
                return invertersContent.find(
                    (inverter) =>
                        inverter.manufacturerName === search.manufacturerName &&
                        inverter.moduleType === search.moduleType,
                );
            } else {
                return invertersContent.find(
                    (inverter) => inverter.zerezId === search.zerezId,
                );
            }
        },
        /**
         * Updates all fields included in the inverter, based on which ones are currently displayed.
         * Also, only updates values that have actually changed, to avoid potentially triggering watchers unintentionally
         * @param inverter
         */
        updateInverterFields(inverter: InverterDataContent) {
            const showingDropdownFields =
                this.model.inverterManualInputManufacturerTypeZerezId
                    .disabled ||
                !this.model.inverterManualInputManufacturerTypeZerezId.value;

            // Manufacturer Name
            if (
                inverter.manufacturerName !==
                this.model.inverterManufacturerAndModuleType.manufacturer.value
            ) {
                if (showingDropdownFields) {
                    this.model.inverterManufacturerAndModuleType.manufacturerSelect.value =
                        inverter.manufacturerName;
                } else {
                    this.model.inverterManufacturerAndModuleType.manufacturerText.value =
                        inverter.manufacturerName;
                }
            }

            // Manufacturer Module Type
            if (
                inverter.moduleType !==
                this.model.inverterManufacturerAndModuleType.moduleType.value
            ) {
                if (showingDropdownFields) {
                    this.model.inverterManufacturerAndModuleType.moduleTypeSelect.value =
                        inverter.moduleType;
                } else {
                    this.model.inverterManufacturerAndModuleType.moduleTypeText.value =
                        inverter.moduleType;
                }
            }

            // Zerez Id
            if (
                inverter.zerezId !==
                this.model.inverterZerezIdAndNorm.zerezId.value
            ) {
                this.model.inverterZerezIdAndNorm.zerezId.value =
                    inverter.zerezId;
            }

            // Norm
            if (
                inverter.connectionRulesTypes !==
                this.model.inverterZerezIdAndNorm.norm.value
            ) {
                this.model.inverterZerezIdAndNorm.norm.value =
                    inverter.connectionRulesTypes;
            }

            // Active Power
            const parsedActivePower = formatGermanNumber(inverter.activePower, {
                decimals: 2,
                thousandsSeparator: true,
            });
            if (
                parsedActivePower !==
                this.model.inverterActivePowerAndApparentPower.activePower.value
            ) {
                this.model.inverterActivePowerAndApparentPower.activePower.value = parsedActivePower;
            }

            // Apparent Power
            const parsedApparentPower = formatGermanNumber(
                inverter.apparentPower,
                { decimals: 2, thousandsSeparator: true },
            );
            if (
                parsedApparentPower !==
                this.model.inverterActivePowerAndApparentPower.apparentPower
                    .value
            ) {
                this.model.inverterActivePowerAndApparentPower.apparentPower.value = parsedApparentPower;
            }
        },
    },
});
